import React from "react";
import {
  Drawer,
  Dialog,
  Box,
  Typography,
  IconButton,
  TextField,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import CampaignIcon from "@mui/icons-material/Campaign";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  fetchMessagingThreads,
  fetchMessagingThread,
  sendMessagingMessage,
  markThreadRead,
} from "../../api/apiClient";

function initials(name) {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");
}

// Selects the threads relevant to this class from the full threads payload.
//
// Judgment call: broadcast threads carry `classId` directly (per
// MessagingService#threadToMap, `m.put("classId", ...)` is set for every
// thread kind, not just broadcasts), so a strict `classId` match would in
// theory work for DMs too. However DM threads are 1:1 conversations that can
// be reused across classes the two users share, so relying purely on
// `classId` risks under- or over-scoping if that field's population isn't
// perfectly consistent for DMs. To be robust either way, DM threads are
// included if EITHER the classId matches OR at least one participant's name
// matches a roster member of this class — participants only carry `{name,
// role}` (no userId) per `threadToMap`, so name is the only join key
// available client-side without an extra endpoint call.
//
// Known limitation: two roster members sharing the same display name are
// indistinguishable by this heuristic, so a DM with a same-named student
// from a different class could false-positive into this list if that DM's
// classId isn't set. Acceptable for a first pass; a userId-bearing
// participants field from the backend would remove this risk entirely.
export function filterThreadsForClass(threadsPayload, classId, roster) {
  const threads = threadsPayload?.threads || {};
  const order = threadsPayload?.order || { broadcast: [], dm: [], group: [] };
  const classIdStr = classId != null ? String(classId) : null;
  const rosterNames = new Set(
    (roster || []).map((r) => (r.name || "").trim().toLowerCase()).filter(Boolean)
  );

  const broadcast = order.broadcast
    .map((id) => threads[id])
    .filter((t) => t && classIdStr != null && t.classId === classIdStr);

  const dm = order.dm.map((id) => threads[id]).filter((t) => {
    if (!t) return false;
    if (classIdStr != null && t.classId === classIdStr) return true;
    return (t.participants || []).some((p) =>
      rosterNames.has((p.name || "").trim().toLowerCase())
    );
  });

  return { broadcast, dm };
}

export default function MessagingDrawer({
  open,
  onClose,
  classId,
  roster,
  currentUser, // reserved for a later phase (e.g. sender-highlighting); not read yet
  onOpenFullInbox,
  initialThreadId,
  presentation = "drawer",
  T,
}) {
  const [loadingList, setLoadingList] = React.useState(false);
  const [listError, setListError] = React.useState(false);
  const [relevantThreads, setRelevantThreads] = React.useState({ broadcast: [], dm: [] });

  const [activeThreadId, setActiveThreadId] = React.useState(null);
  const [activeThread, setActiveThread] = React.useState(null);
  const [loadingThread, setLoadingThread] = React.useState(false);
  const [threadError, setThreadError] = React.useState(false);

  const [replyText, setReplyText] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [sendError, setSendError] = React.useState(false);

  const messagesRef = React.useRef(null);

  // Kept in a ref (rather than a dep) so the fetch below only re-runs on open/classId
  // changes, not on every roster re-render — roster is read at fetch time via the ref.
  const rosterRef = React.useRef(roster);
  rosterRef.current = roster;

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingList(true);
    setListError(false);
    fetchMessagingThreads()
      .then((data) => {
        if (cancelled) return;
        setRelevantThreads(filterThreadsForClass(data, classId, rosterRef.current));
      })
      .catch(() => {
        if (!cancelled) setListError(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, classId]);

  React.useEffect(() => {
    if (!open) {
      setActiveThreadId(null);
      setActiveThread(null);
      setReplyText("");
      setSendError(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (open && initialThreadId) {
      openThread(initialThreadId);
    }
  }, [open, initialThreadId]);

  React.useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [activeThread]);

  function openThread(threadId) {
    setActiveThreadId(threadId);
    setActiveThread(null);
    setThreadError(false);
    setReplyText("");
    setSendError(false);
    setLoadingThread(true);
    fetchMessagingThread(threadId)
      .then((thread) => {
        setActiveThread(thread);
      })
      .catch(() => {
        setThreadError(true);
      })
      .finally(() => {
        setLoadingThread(false);
      });
    markThreadRead(threadId).catch(() => {});
    setRelevantThreads((prev) => ({
      broadcast: prev.broadcast.map((t) => (t.id === threadId ? { ...t, unread: 0 } : t)),
      dm: prev.dm.map((t) => (t.id === threadId ? { ...t, unread: 0 } : t)),
    }));
  }

  function backToList() {
    setActiveThreadId(null);
    setActiveThread(null);
  }

  async function handleSend() {
    const text = replyText.trim();
    if (!text || !activeThread || sending) return;
    setSending(true);
    setSendError(false);
    try {
      const savedMsg = await sendMessagingMessage(activeThread.id, text);
      setActiveThread((prev) =>
        prev ? { ...prev, messages: [...(prev.messages || []), savedMsg] } : prev
      );
      setReplyText("");
    } catch (err) {
      console.error("Failed to send message:", err);
      setSendError(true);
    } finally {
      setSending(false);
    }
  }

  function handleReplyKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleOpenFullInbox() {
    onClose();
    if (onOpenFullInbox) onOpenFullInbox(activeThreadId);
  }

  const { broadcast, dm } = relevantThreads;
  const hasThreads = broadcast.length > 0 || dm.length > 0;
  const isModal = presentation === "modal";
  const dialogTitleId = "messaging-surface-title";
  const Container = isModal ? Dialog : Drawer;
  const containerProps = isModal
    ? {
        open,
        onClose,
        "aria-labelledby": dialogTitleId,
        maxWidth: false,
        fullWidth: true,
        PaperProps: {
          "data-testid": "messaging-modal-paper",
          sx: {
            width: { xs: "calc(100% - 32px)", sm: 700 },
            maxWidth: 700,
            height: { xs: "calc(100% - 32px)", sm: "min(720px, calc(100% - 64px))" },
            m: { xs: 2, sm: 4 },
          },
        },
      }
    : { anchor: "right", open, onClose };

  return (
    <Container {...containerProps}>
      <Box
        sx={{
          width: isModal ? "100%" : { xs: 340, sm: 380 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: T.surface,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            px: "16px",
            py: "14px",
            borderBottom: `1px solid ${T.line}`,
            flexShrink: 0,
          }}
        >
          {activeThreadId && (
            <IconButton size="small" onClick={backToList} sx={{ color: T.ink3 }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography id={dialogTitleId} sx={{ flex: 1, fontSize: 16, fontWeight: 800, color: T.ink, letterSpacing: "-.01em", fontFamily: "var(--font-display)" }}>
            {activeThread ? activeThread.title : "Messages"}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ color: T.ink3 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        {!activeThreadId ? (
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {loadingList ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: "40px" }}>
                <CircularProgress size={24} />
              </Box>
            ) : listError ? (
              <Box sx={{ p: "24px 16px", textAlign: "center", color: T.danger || "#B42318", fontSize: 13.5 }}>
                Failed to load conversations.
              </Box>
            ) : !hasThreads ? (
              <Box sx={{ p: "32px 20px", textAlign: "center", color: T.ink4, fontSize: 13.5 }}>
                No conversations yet for this class.
              </Box>
            ) : (
              <>
                {broadcast.length > 0 && (
                  <Box sx={{ px: "16px", pt: "14px", pb: "6px", fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.ink4 }}>
                    Class Broadcast
                  </Box>
                )}
                {broadcast.map((t) => (
                  <ThreadRow key={t.id} thread={t} onClick={() => openThread(t.id)} T={T} />
                ))}
                {dm.length > 0 && (
                  <Box sx={{ px: "16px", pt: "14px", pb: "6px", fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.ink4 }}>
                    Direct Messages
                  </Box>
                )}
                {dm.map((t) => (
                  <ThreadRow key={t.id} thread={t} onClick={() => openThread(t.id)} T={T} />
                ))}
              </>
            )}
          </Box>
        ) : (
          <>
            <Box ref={messagesRef} sx={{ flex: 1, overflowY: "auto", p: "12px 14px", display: "flex", flexDirection: "column", gap: "10px" }}>
              {loadingThread ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: "40px" }}>
                  <CircularProgress size={22} />
                </Box>
              ) : threadError ? (
                <Box sx={{ p: "20px", textAlign: "center", color: T.danger || "#B42318", fontSize: 13.5 }}>
                  Failed to load this conversation.
                </Box>
              ) : (activeThread?.messages || []).length === 0 ? (
                <Box sx={{ p: "20px", textAlign: "center", color: T.ink4, fontSize: 13.5 }}>
                  No messages yet.
                </Box>
              ) : (
                (activeThread.messages || []).map((m) => (
                  <MessageBubble key={m.id} message={m} T={T} />
                ))
              )}
            </Box>

            {sendError && (
              <Box sx={{ px: "14px", pb: "6px", fontSize: 12, color: T.danger || "#B42318" }}>
                Failed to send. Your message was not delivered — try again.
              </Box>
            )}

            <Box sx={{ borderTop: `1px solid ${T.line2}`, p: "10px 12px", display: "flex", gap: "8px", alignItems: "flex-end", flexShrink: 0 }}>
              <TextField
                size="small"
                fullWidth
                multiline
                maxRows={4}
                placeholder={activeThread?.placeholder || "Type a message…"}
                value={replyText}
                onChange={(e) => {
                  setReplyText(e.target.value);
                  if (sendError) setSendError(false);
                }}
                onKeyDown={handleReplyKeyDown}
                disabled={!activeThread || loadingThread}
              />
              <IconButton
                onClick={handleSend}
                disabled={!replyText.trim() || sending || !activeThread}
                sx={{
                  bgcolor: T.primary,
                  color: "#fff",
                  "&:hover": { bgcolor: T.p700 || T.primary },
                  "&.Mui-disabled": { bgcolor: T.surface3, color: T.ink4 },
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Box>
          </>
        )}

        {/* Footer */}
        <Box
          onClick={handleOpenFullInbox}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            p: "12px",
            borderTop: `1px solid ${T.line}`,
            fontSize: 13,
            fontWeight: 700,
            color: T.primary,
            cursor: "pointer",
            flexShrink: 0,
            "&:hover": { bgcolor: T.surface2 },
          }}
        >
          Open full inbox <OpenInNewIcon sx={{ fontSize: 15 }} />
        </Box>
      </Box>
    </Container>
  );
}

function ThreadRow({ thread, onClick, T }) {
  const lastMsg = (thread.messages || [])[thread.messages.length - 1];
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        px: "16px",
        py: "10px",
        cursor: "pointer",
        borderBottom: `1px solid ${T.line2}`,
        "&:hover": { bgcolor: T.surface2 },
      }}
    >
      {thread.kind === "broadcast" ? (
        <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: T.teal, display: "grid", placeItems: "center", color: "#fff", flexShrink: 0 }}>
          <CampaignIcon sx={{ fontSize: 18 }} />
        </Box>
      ) : (
        <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: T.primary, color: "#fff", display: "grid", placeItems: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
          {initials(thread.title)}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: thread.unread > 0 ? 700 : 600, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
            {thread.title}
          </Typography>
          {thread.unread > 0 && (
            <Box sx={{ bgcolor: T.teal, color: "#fff", fontSize: 10.5, fontWeight: 700, minWidth: 18, height: 18, px: "5px", borderRadius: "9px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              {thread.unread}
            </Box>
          )}
        </Box>
        <Typography sx={{ fontSize: 12, color: T.ink3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {thread.subtitle || lastMsg?.text || ""}
        </Typography>
      </Box>
    </Box>
  );
}

function MessageBubble({ message, T }) {
  const own = !!message.own;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: own ? "flex-end" : "flex-start" }}>
      {!own && (
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.ink3, mb: "2px", px: "4px" }}>
          {message.sender}
        </Typography>
      )}
      <Box
        sx={{
          maxWidth: "80%",
          bgcolor: own ? T.primary : T.surface3,
          color: own ? "#fff" : T.ink,
          borderRadius: own ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
          px: "12px",
          py: "8px",
          fontSize: 13.5,
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {message.text}
      </Box>
      <Typography sx={{ fontSize: 10.5, color: T.ink4, mt: "2px", px: "4px" }}>
        {message.time}
      </Typography>
    </Box>
  );
}
